import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { localizationApi, type EffectiveLocalization, type LanguageDto } from '@/api/localization.api';
import { useAuth } from '@/auth/useAuth';

const STORAGE_KEY = 'st.lang';

/** System defaults — mirror the backend LocalizationDefaults so the app renders before settings load. */
const DEFAULT_EFFECTIVE: EffectiveLocalization = {
  language: 'en', locale: 'en-US', country: null, currency: 'USD', timeZone: 'UTC',
  dateFormat: 'dd MMM yyyy', timeFormat: 'HH:mm', numberFormat: '#,##0.##', firstDayOfWeek: 1, measurementSystem: 'Metric', rtl: false,
};

export interface I18nContextValue {
  /** Translate a key with optional {{var}} interpolation. Falls back to the key when missing. */
  t: (key: string, vars?: Record<string, string | number>) => string;
  lang: string;
  setLang: (code: string) => void;
  dir: 'ltr' | 'rtl';
  languages: LanguageDto[];
  effective: EffectiveLocalization;
  isReady: boolean;
}

export const I18nContext = createContext<I18nContextValue | null>(null);

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (k in vars ? String(vars[k]) : `{{${k}}}`));
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const qc = useQueryClient();
  const [override, setOverride] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));

  const effectiveQuery = useQuery({
    queryKey: ['localization', 'effective'],
    queryFn: localizationApi.effective,
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });
  const languagesQuery = useQuery({
    queryKey: ['localization', 'enabled-languages'],
    queryFn: localizationApi.enabledLanguages,
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });

  const effective = effectiveQuery.data ?? DEFAULT_EFFECTIVE;
  const lang = override ?? effective.language ?? 'en';

  const packQuery = useQuery({
    queryKey: ['localization', 'pack', lang],
    queryFn: () => localizationApi.pack(lang),
    enabled: isAuthenticated && !!lang,
    staleTime: 5 * 60_000,
  });
  const resources = packQuery.data?.resources ?? {};
  const dir: 'ltr' | 'rtl' = (packQuery.data?.rtl ?? effective.rtl) ? 'rtl' : 'ltr';

  // Apply document direction + lang for RTL languages and screen readers.
  useEffect(() => {
    document.documentElement.setAttribute('lang', lang);
    document.documentElement.setAttribute('dir', dir);
  }, [lang, dir]);

  const setLang = useCallback((code: string) => {
    setOverride(code);
    localStorage.setItem(STORAGE_KEY, code);
    // Persist as a user preference (best-effort — the UI switches instantly regardless).
    if (isAuthenticated) {
      localizationApi.updateUserSettings({ language: code }).then(() => {
        qc.invalidateQueries({ queryKey: ['localization', 'effective'] });
        qc.invalidateQueries({ queryKey: ['rtl', 'effective'] }); // re-resolve direction for the new language (Phase 14.7)
      }).catch(() => { /* non-blocking */ });
    }
  }, [isAuthenticated, qc]);

  const t = useCallback((key: string, vars?: Record<string, string | number>): string => {
    const value = resources[key];
    return value != null ? interpolate(value, vars) : key;
  }, [resources]);

  const value = useMemo<I18nContextValue>(() => ({
    t, lang, setLang, dir, languages: languagesQuery.data ?? [], effective, isReady: !isAuthenticated || (effectiveQuery.isSuccess && packQuery.isFetched),
  }), [t, lang, setLang, dir, languagesQuery.data, effective, isAuthenticated, effectiveQuery.isSuccess, packQuery.isFetched]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
